import { Progress } from "@/components/ui/progress";

const eur = (cents: number) =>
  `€${(cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export type RentOpenBalanceLabels = {
  partiallyPaid: string;
  openStatus: string;
  paidOf: (paid: string, billed: string) => string;
  openRent: string;
  dunningFees: string;
  totalOpen: string;
  outstandingFees: string;
};

/**
 * COL-472: the open balance on the tenant's rent card — a part-paid month
 * ("EUR 300 of EUR 650 paid") and any Mahngebühren on top, so the card says
 * what is actually still owed instead of only the monthly rent.
 *
 * Fees join the month's total only when that month carries them
 * (`feesOnMonth`, see `feeCarrierPaymentId`); otherwise they are listed on
 * their own line, so they are never folded into a month not due yet.
 *
 * Renders nothing when the month is untouched and no fees are owed.
 */
export function RentOpenBalance({
  monthLabel,
  expectedCents,
  receivedCents,
  remainingCents,
  isPartial,
  feeCents,
  feesOnMonth,
  labels,
}: {
  /** The open month, e.g. "September 2026"; null when only fees are open. */
  monthLabel: string | null;
  expectedCents: number;
  receivedCents: number;
  remainingCents: number;
  isPartial: boolean;
  /** The booking's whole fee pool. */
  feeCents: number;
  /** The shown month is the one that carries `feeCents`. */
  feesOnMonth: boolean;
  labels: RentOpenBalanceLabels;
}) {
  const showMonth = monthLabel != null;
  const monthFees = showMonth && feesOnMonth ? feeCents : 0;
  const standaloneFees = feeCents - monthFees;
  if (!isPartial && feeCents <= 0) return null;
  const pct =
    expectedCents > 0
      ? Math.max(
          3,
          Math.min(100, Math.round((receivedCents / expectedCents) * 100))
        )
      : 0;
  return (
    <div className="space-y-2 pt-2 border-t border-border/40">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          {monthLabel ?? labels.outstandingFees}
        </p>
        {showMonth && (
          <span
            className={
              isPartial
                ? "rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700"
                : "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
            }
          >
            {isPartial ? labels.partiallyPaid : labels.openStatus}
          </span>
        )}
      </div>
      {isPartial && (
        <>
          <Progress
            value={pct}
            aria-label={labels.paidOf(eur(receivedCents), eur(expectedCents))}
            className="h-1.5 bg-muted [&>div]:bg-blue-500 rounded-full"
          />
          <p className="text-xs text-muted-foreground">
            {labels.paidOf(eur(receivedCents), eur(expectedCents))}
          </p>
        </>
      )}
      <dl className="space-y-1 text-xs tabular-nums">
        {showMonth && (
          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted-foreground">{labels.openRent}</dt>
            <dd className="font-semibold text-foreground">
              {eur(remainingCents)}
            </dd>
          </div>
        )}
        {monthFees > 0 && (
          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted-foreground">{labels.dunningFees}</dt>
            <dd className="font-semibold text-amber-700 dark:text-amber-400">
              {eur(monthFees)}
            </dd>
          </div>
        )}
        {monthFees > 0 && (
          <div className="flex items-center justify-between gap-2 border-t border-border/40 pt-1">
            <dt className="font-semibold text-foreground">
              {labels.totalOpen}
            </dt>
            <dd className="font-bold text-foreground">
              {eur(remainingCents + monthFees)}
            </dd>
          </div>
        )}
        {standaloneFees > 0 && (
          <div className="flex items-center justify-between gap-2">
            <dt className="text-muted-foreground">
              {showMonth ? labels.outstandingFees : labels.dunningFees}
            </dt>
            <dd className="font-semibold text-amber-700 dark:text-amber-400">
              {eur(standaloneFees)}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
